import type {
  EmailSignatureBannerId,
  EmailSignatureCardOptionToImportOrLink,
  EmailSignatureCrop,
  EmailSignatureImageFieldId,
  EmailSignatureImagesTypes,
  EmailSignatureShapes,
  EmailSignatureV2,
} from '@components/EmailSignaturev2/types';
import type { ProfileOption } from '@components/Profile/UtilsComponents/ProfilesSelect';
import { logger } from '@modules/logger';
import {
  type EmailSignaturesActions,
  initialEmailSignatureContextValues,
} from '@reducers/email-signature-reducer';
import { createContext } from 'react';

const emailSignatureSideEditorServicesContext = createContext<{
  emailSignature: EmailSignatureV2;
  emailSignatureDispatch: (action: EmailSignaturesActions) => void;
  emailSignatureHasBeenLoaded: boolean;
  isEditingSavedEmailSignature: boolean;
  isSignatureLocked: boolean;
  localCroppedBannerImageUrl: string | undefined;
  handleCroppedBannerImageChange: (
    imageReference: string,
    crop: EmailSignatureCrop | null,
    bannerId: EmailSignatureBannerId,
  ) => void;
  isEverythingEmpty: boolean;
  hasEditedFieldWidth: boolean;
  onEditFieldWidth: () => void;
  isInTransitionState: boolean;
  originalBannerImageUrl: string;
  handleUploadOriginalBannerImageUrl: (imageReference: string | null, bannerId: string) => void;
  displayBannerInViewMode: boolean;
  handleChangeBannerViewMode: (newDisplaySavedBannerImageContainer: boolean) => void;
  localCroppedImagesUrls: Record<EmailSignatureImageFieldId, string>;
  originalImagesUrls: Record<EmailSignatureImageFieldId, string>;
  handleUpdateLocalCroppedImage: (
    newLocalImageUrl: string,
    imageId: EmailSignatureImageFieldId,
    shape: EmailSignatureShapes,
    crop: EmailSignatureCrop | null,
  ) => void;
  handleUpdateImageShape: (
    imageId: EmailSignatureImageFieldId,
    shape: EmailSignatureShapes,
    crop: EmailSignatureCrop | null,
  ) => void;
  handleUploadOriginalImageUrl: (
    imageReference: string,
    imageId: EmailSignatureImageFieldId,
  ) => void;
  displayImagesInViewMode: Record<EmailSignatureImageFieldId, boolean>;
  handleChangeImageViewMode: (
    newDisplayValue: boolean,
    imageId: EmailSignatureImageFieldId,
  ) => void;
  addCardImageToSignature: (
    imageUrl: string,
    isPrimary: boolean,
    type: EmailSignatureImagesTypes,
  ) => Promise<void>;
}>({
  emailSignature: initialEmailSignatureContextValues,
  emailSignatureDispatch: () =>
    logger.warn('Warning! emailSignatureDispatch not provided for cardContext'),
  emailSignatureHasBeenLoaded: false,
  isEditingSavedEmailSignature: false,
  isSignatureLocked: false,
  localCroppedBannerImageUrl: '',
  handleCroppedBannerImageChange: () =>
    logger.warn('Warning: handleCroppedBannerImageChange is not implemented'),
  isEverythingEmpty: true,
  hasEditedFieldWidth: false,
  onEditFieldWidth: () => logger.warn('Warning: onEditFieldWidth is not implemented'),
  isInTransitionState: true,
  originalBannerImageUrl: '',
  handleUploadOriginalBannerImageUrl: () =>
    logger.warn('handleUploadOriginalBannerImageUrl not provided for cardContext'),
  displayBannerInViewMode: false,
  handleChangeBannerViewMode: () =>
    logger.warn('handleChangeBannerViewMode not provided for cardContext'),
  localCroppedImagesUrls: {},
  originalImagesUrls: {},
  handleUpdateLocalCroppedImage: () =>
    logger.warn('handleUpdateLocalCroppedImage not provided for cardContext'),
  handleUpdateImageShape: () => logger.warn('handleUpdateImageShape not provided for cardContext'),
  handleUploadOriginalImageUrl: () =>
    logger.warn('handleUploadOriginalImageUrl not provided for cardContext'),
  displayImagesInViewMode: {},
  handleChangeImageViewMode: () =>
    logger.warn('handleChangeImageViewMode not provided for cardContext'),
  addCardImageToSignature: async () =>
    logger.warn('addCardImageToSignature not provided for cardContext'),
});

const emailSignatureSideEditorAutoFillServicesContext = createContext<{
  selectedCardOption: EmailSignatureCardOptionToImportOrLink | null;
  handleChangingCard: (card: EmailSignatureCardOptionToImportOrLink | null) => Promise<void>;
  handleUpdateSelectedCardOption: (
    newSelectedCardOption: EmailSignatureCardOptionToImportOrLink | null,
  ) => void;
  onSelectProfileOption: (profileOption: ProfileOption | null) => void;
  selectedProfileOption: ProfileOption | null;
  handleSelectProfileOption: (profileOption: ProfileOption | null) => void;
  visibleCardOptions: EmailSignatureCardOptionToImportOrLink[];
}>({
  selectedCardOption: null,
  handleChangingCard: async () => logger.warn('handleChangingCard not provided for cardContext'),
  handleUpdateSelectedCardOption: () =>
    logger.warn('handleUpdateSelectedCardOption not provided for cardContext'),
  onSelectProfileOption: () => logger.warn('onSelectProfileOption not provided for cardContext'),
  selectedProfileOption: null,
  handleSelectProfileOption: () =>
    logger.warn('handleSelectProfileOption not provided for cardContext'),
  visibleCardOptions: [],
});

export { emailSignatureSideEditorAutoFillServicesContext, emailSignatureSideEditorServicesContext };
